import { useState } from "react";
import { previewOperation } from "@core/api/queries";
import type { MessageView } from "@core/api/types";
import { Chips } from "@core/components/List";
import { ErrorNote } from "@core/components/ErrorNote";
import { MessageList, useQuickAction } from "@core/components/MessageList";
import { PlacementTree, type PlacementNode } from "@core/components/Placement";
import { Sheet } from "@core/components/Sheet";
import { Value } from "@core/components/Value";
import { number } from "@core/text";
import { CreatureField, InventoryRow, useClearErrorOnClose } from "./inventory";
import { resourceLabel, useSm, type InventoryView, type PlaceView } from "./view";

type KindFilter = "weapons" | "armor" | "relics" | "items" | "ammo";

const isGoods = (i: InventoryView) => i.kind === "item" || i.kind === "custom";
const isRelicOrArtifact = (i: InventoryView) => i.relic !== null || i.artifact !== null;

/** Weapons, armour and shields stay in their group as relic or artifact too, so nothing shows twice. */
const kindFilters: { value: KindFilter; label: string; matches: (item: InventoryView) => boolean }[] = [
  { value: "weapons", label: "Waffen", matches: (i) => i.kind === "weapon" },
  { value: "armor", label: "Rüstungen und Schilde", matches: (i) => i.kind === "armor" || i.kind === "shield" },
  { value: "relics", label: "Relikte und Artefakte", matches: (i) => isGoods(i) && isRelicOrArtifact(i) },
  { value: "items", label: "Gegenstände", matches: (i) => isGoods(i) && !isRelicOrArtifact(i) },
  { value: "ammo", label: "Munition", matches: (i) => i.kind === "ammo" },
];

/** The hints about a place or a carrier, e.g. a Zuflucht no longer held or a pack animal over its capacity, with their quick action. */
function LocationMessages(props: { id: string }) {
  const { character } = useSm();
  const runAction = useQuickAction();
  const messages = character.evaluation.messages.filter((m: MessageView) => m.message.target?.path === `ausruestung.ort.${props.id}`);
  return <MessageList compact messages={messages} onAction={runAction} />;
}

/**
 * The equipment by location (E44, docs/konzept-ort-und-traglast.md): body, pack animals and vehicles, storage and places, with containers
 * nested and carriers standing in storage or at a place below it. Whether a piece is carried follows from where it lies; to storage and
 * back is a move like any other, with a shortcut in the row.
 */
export function InventoryTree(props: { onEdit: (item: InventoryView) => void }) {
  const { view, character, apply, choose, confirm } = useSm();
  const [filter, setFilter] = useState<KindFilter | null>(null);
  const [query, setQuery] = useState("");
  const [carrier, setCarrier] = useState<InventoryView | "new" | null>(null);
  const [place, setPlace] = useState<PlaceView | "new" | null>(null);
  const byInstance = new Map(view.inventory.map((i) => [i.instance, i]));
  const places = new Map(view.places.map((p) => [p.id, p]));
  const kind = kindFilters.find((f) => f.value === filter)?.matches;
  const term = query.trim().toLocaleLowerCase("de");
  const matches = (item: InventoryView | undefined) =>
    item !== undefined && (!kind || kind(item)) && (!term || [item.customName, item.name].some((n) => n?.toLocaleLowerCase("de").includes(term)));
  const nodes: PlacementNode[] = view.placement.nodes;

  /** Moves at once with undo; a move that changes the handicap, e.g. taking off armour, shows the preview first. */
  const move = async (ids: string[], target: string, quantity?: number) => {
    const operation = { type: "sm.moveItems", payload: quantity === undefined ? { instances: ids, target } : { instances: ids, target, quantity } };
    const preview = await previewOperation(character.id, operation).catch(() => null);
    // The selection stays until the preview is confirmed; cancelling keeps it for another target.
    if (preview?.changes.some((c) => c.key === "behinderung")) return confirm(operation, "Verschieben");
    return choose(operation, "Verschoben");
  };

  return (
    <>
      <PlacementTree
        nodes={nodes}
        storageKey={`splittertraeger.ort.${character.id}`}
        filter={kind || term ? (node) => matches(byInstance.get(node.id)) : null}
        onMove={move}
        toolbar={
          <>
            <div className="inventory-search">
              <input type="search" placeholder="Inventar filtern …" aria-label="Inventar filtern" value={query} onChange={(e) => setQuery(e.target.value)} />
              {query && (
                <button type="button" className="icon-button" aria-label="Filter leeren" title="Filter leeren" onClick={() => setQuery("")}>
                  ×
                </button>
              )}
            </div>
            <Chips ariaLabel="Art" allLabel="Alle" options={kindFilters} value={filter} onChange={setFilter} />
          </>
        }
        rootActions={(root) => {
          const item = byInstance.get(root.id);
          const at = places.get(root.id);
          if (at) {
            return (
              <button type="button" className="link small" aria-label={`Stätte ${root.label} bearbeiten`} onClick={() => setPlace(at)}>
                Bearbeiten
              </button>
            );
          }
          return (
            item &&
            root.role === "carrier" && (
              <button type="button" className="link small" aria-label={`Lasttier ${root.label} bearbeiten`} onClick={() => setCarrier(item)}>
                Bearbeiten
              </button>
            )
          );
        }}
        rootDetails={(root) => {
          const at = places.get(root.id);
          return (
            <>
              {at && (at.levelKey || at.note) && (
                <p className="muted small place-details">
                  {at.levelKey && (
                    <>
                      Stufe <Value valueKey={at.levelKey} />
                    </>
                  )}
                  {at.levelKey && at.note && " · "}
                  {at.note}
                </p>
              )}
              {(at || root.role === "carrier") && <LocationMessages id={root.id} />}
            </>
          );
        }}
        renderRow={(node, context) => {
          const item = byInstance.get(node.id);
          if (!item) return null;
          // A carrier standing in storage or at a place: its row opens the sheet of the carrier, „Mitnehmen“ makes it a location of its own.
          const standing = node.role === "carrier";
          return (
            <>
              <InventoryRow
                as="div"
                item={item}
                showOrigin={character.mode === "creation"}
                container={context.path.at(-1)}
                onEdit={standing ? setCarrier : props.onEdit}
                actions={
                  context.selecting ? undefined : (
                    <>
                      {item.wearable && (
                        <label className="switch">
                          <input
                            type="checkbox"
                            role="switch"
                            checked={item.equipped}
                            aria-label={`${item.customName ?? item.name} angelegt`}
                            onChange={(e) =>
                              void choose({ type: "sm.setEquipped", payload: { instance: item.instance, equipped: e.target.checked } }, e.target.checked ? "Angelegt" : "Abgelegt")
                            }
                          />
                          <span>angelegt</span>
                        </label>
                      )}
                      {item.needsSetup && (
                        <button type="button" className="button secondary small" onClick={() => void apply({ type: "sm.setupCarrier", payload: { instance: item.instance } })}>
                          Als eigenen Ort einrichten
                        </button>
                      )}
                      {context.shortcut}
                      {context.moveButton}
                    </>
                  )
                }
              />
              {standing && <LocationMessages id={node.id} />}
            </>
          );
        }}
      />
      <div className="actions">
        <button type="button" className="button secondary" onClick={() => setCarrier("new")}>
          + Lasttier oder Fahrzeug
        </button>
        <button type="button" className="button secondary" onClick={() => setPlace("new")}>
          + Stätte
        </button>
      </div>
      {carrier && <CarrierSheet item={carrier === "new" ? null : carrier} onClose={() => setCarrier(null)} onEditItem={props.onEdit} />}
      {place && <PlaceSheet place={place === "new" ? null : place} onClose={() => setPlace(null)} />}
    </>
  );
}

/**
 * A place besides body and storage (O5): a free one with its own name, or an instance of Zuflucht set up as a place, whose name and level
 * follow the instance (GRW p. 79–80). Things there count towards no load; removing the place puts everything there into storage, pack
 * animals with their load included.
 */
function PlaceSheet(props: { place: PlaceView | null; onClose: () => void }) {
  const { view, apply, error, clearError } = useSm();
  const place = props.place;
  // Instances of Zuflucht not yet set up as a place; a new place may be one of them instead of a free one.
  const offered = view.resources.filter((r) => r.placeable && !r.place);
  const [tie, setTie] = useState("");
  const [name, setName] = useState(place?.name ?? (place && !place.held ? place.label : ""));
  const [note, setNote] = useState(place?.note ?? "");
  const [busy, setBusy] = useState(false);
  useClearErrorOnClose(clearError);
  // The name of a tied place follows its instance; only a free place, or one whose instance is gone, gets a name of its own.
  const named = place ? !place.held : !tie;
  const run = async (operation: { type: string; payload: Record<string, unknown> }) => {
    if (busy) return;
    setBusy(true);
    try {
      if (await apply(operation)) props.onClose();
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    if (place) {
      const payload: Record<string, unknown> = { id: place.id, note: note.trim() || null };
      if (named && name.trim() !== place.label) payload.name = name.trim();
      void run({ type: "sm.updatePlace", payload });
    } else {
      void run({ type: "sm.addPlace", payload: tie ? { ref: tie, note: note.trim() || null } : { name: name.trim(), note: note.trim() || null } });
    }
  };
  return (
    <Sheet
      title={place ? place.label : "Neue Stätte"}
      onClose={props.onClose}
      footer={
        <>
          {place && (
            <button type="button" className="button danger" disabled={busy} onClick={() => void run({ type: "sm.removePlace", payload: { id: place.id } })}>
              Entfernen
            </button>
          )}
          <button type="button" className="button primary" disabled={busy || (named && !name.trim())} onClick={save}>
            {place ? "Speichern" : "Anlegen"}
          </button>
        </>
      }
    >
      <div className="stack small-gap">
        <ErrorNote error={error} />
        {!place && offered.length > 0 && (
          <label className="field">
            <span>Art</span>
            <select value={tie} onChange={(e) => setTie(e.target.value)}>
              <option value="">Freie Stätte</option>
              {offered.map((r) => (
                <option key={r.key} value={r.key}>
                  {resourceLabel(view, r.key)}
                </option>
              ))}
            </select>
          </label>
        )}
        {named ? (
          <label className="field">
            <span>Name</span>
            <input value={name} placeholder="z. B. Versteck im Wald" onChange={(e) => setName(e.target.value)} />
          </label>
        ) : (
          <p className="muted small">Name und Stufe folgen der Ressource; umbenannt wird sie bei den Ressourcen.</p>
        )}
        {place?.ref && !place.held && <p className="muted small">Die Ressource hat der Charakter nicht mehr. Mit einem eigenen Namen wird es eine freie Stätte.</p>}
        <label className="field">
          <span>Notiz</span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p className="muted small">Was an einer Stätte liegt, zählt zu keiner Traglast und wirkt nicht; sein Wert zählt weiter. Lasttiere und Fahrzeuge dürfen dort stehen.</p>
        {place && place.count > 0 && (
          <p className="muted small">
            Beim Entfernen kommt alles, was dort liegt oder steht ({place.count === 1 ? "1 Gegenstand" : `${place.count} Gegenstände`}), ins Lager.
          </p>
        )}
      </div>
    </Sheet>
  );
}

/**
 * A pack animal or vehicle as a location of its own: name, strength and capacity as inputs (SM-G-097), optionally the instance of Kreatur
 * it comes from instead of being bought (GRW p. 78, SM-G-192). The engine computes load and capacity with their derivation. One the user
 * set up as a carrier can be taken back.
 */
function CarrierSheet(props: { item: InventoryView | null; onClose: () => void; onEditItem: (item: InventoryView) => void }) {
  const { view, apply, choose, error, clearError } = useSm();
  const item = props.item;
  const [name, setName] = useState(item?.customName ?? "");
  const [strength, setStrength] = useState(item?.carrier?.strength == null ? "" : String(item.carrier.strength));
  const [capacity, setCapacity] = useState(item?.carrier?.capacity == null ? "" : number(item.carrier.capacity));
  const [creature, setCreature] = useState(item?.creature ?? "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useClearErrorOnClose(clearError);

  const contents = item ? view.inventory.filter((i) => i.parent === item.instance).length : 0;
  // What a carrier standing at a place carries stays at the place when it goes; otherwise it goes to storage.
  const standsAt = item?.parent ? view.places.find((p) => p.id === item.parent) : undefined;
  const inputs = { strength: strength.trim() ? Number(strength) : null, capacity: capacity.trim() || null, creature: creature || null };
  // Taking the carrier back is an operation of its own; it waits until the inputs are saved, so none of them is lost unnoticed.
  const [savedInputs] = useState(() => JSON.stringify({ name, ...inputs }));
  const dirty = JSON.stringify({ name, ...inputs }) !== savedInputs;
  // Taking the carrier back moves its load, so it says where with undo, as a move does.
  const run = async (operation: { type: string; payload: Record<string, unknown> }, notice?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      if (await (notice ? choose(operation, notice) : apply(operation))) props.onClose();
    } finally {
      setBusy(false);
    }
  };
  const save = () =>
    void run(
      item
        ? { type: "sm.updateCarrier", payload: { instance: item.instance, name: name.trim() || null, ...inputs } }
        : { type: "sm.addCarrier", payload: { name: name.trim(), note: note.trim() || null, ...inputs } },
    );
  return (
    <Sheet
      title={item ? (item.customName ?? item.name) : "Lasttier oder Fahrzeug"}
      onClose={props.onClose}
      footer={
        <>
          {item && (
            <button type="button" className="button danger" disabled={busy} onClick={() => void run({ type: "sm.removeItem", payload: { instance: item.instance } })}>
              Entfernen
            </button>
          )}
          {item && (
            <button
              type="button"
              className="button secondary"
              onClick={() => {
                props.onClose();
                props.onEditItem(item);
              }}
            >
              Preis und Verkauf
            </button>
          )}
          <button type="button" className="button primary" disabled={busy || (!item && !name.trim())} onClick={save}>
            {item ? "Speichern" : "Hinzufügen"}
          </button>
        </>
      }
    >
      <div className="stack small-gap">
        <ErrorNote error={error} />
        <label className="field">
          <span>Name</span>
          <input value={name} placeholder={item ? item.name : "z. B. Maultier Grauohr"} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="inline-form two">
          <label className="field">
            <span>Stärke</span>
            <input inputMode="numeric" value={strength} placeholder="aus der Liste, sonst leer" onChange={(e) => setStrength(e.target.value)} />
          </label>
          <label className="field">
            <span>Traglast</span>
            <input inputMode="decimal" value={capacity} placeholder="sonst aus der Stärke" onChange={(e) => setCapacity(e.target.value)} />
          </label>
        </div>
        <p className="muted small">
          Das Regelwerk nennt keine Traglast für Tiere und Fahrzeuge. Die App rechnet wie beim Abenteurer mit der Stärke (SM-G-097); eine eingegebene Traglast ersetzt das.
          Was darauf liegt, zählt nicht zur eigenen Traglast.
        </p>
        <CreatureField item={item} value={creature} onChange={setCreature} />
        {!item && (
          <label className="field">
            <span>Notiz</span>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        )}
        {item && !item.carrierByList && (
          <div className="inline-form two">
            <p className="muted small">
              {dirty
                ? "Erst speichern; danach lässt es sich zurücknehmen."
                : standsAt
                  ? `Ohne Lasttier bleibt es mit dem, was darauf liegt, an der Stätte ${standsAt.label}.`
                  : item.parent === "storage"
                    ? "Ohne Lasttier bleibt es mit dem, was darauf liegt, im Lager."
                    : "Ohne Lasttier kommt es mit dem, was darauf liegt, ins Lager."}
            </p>
            <button
              type="button"
              className="button secondary"
              disabled={busy || dirty}
              onClick={() => void run({ type: "sm.setCarrier", payload: { instance: item.instance, carrier: false } }, "Kein Lasttier mehr")}
            >
              Kein Lasttier mehr
            </button>
          </div>
        )}
        {item && contents > 0 && (
          <p className="muted small">
            Beim Entfernen {standsAt ? "bleibt" : "kommt"}, was darauf liegt ({contents === 1 ? "1 Gegenstand" : `${contents} Gegenstände`}),{" "}
            {standsAt ? `an der Stätte ${standsAt.label}` : "ins Lager"}.
          </p>
        )}
      </div>
    </Sheet>
  );
}
